import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, 
  Autocomplete, TextField, Typography, Snackbar, Alert, Backdrop, CircularProgress,
  IconButton, Tooltip, Badge, List, ListItem, ListItemText, ListItemSecondaryAction, ListItemIcon
} from '@mui/material'; 
import { 
  Add as AddIcon, Print as PrintIcon, Search as SearchIcon, Draw as DrawIcon, 
  Save as SaveIcon, CloudUpload as CloudUploadIcon, PhotoCamera as PhotoIcon, 
  ExitToApp as ExitIcon, Description as TemplateIcon, ContentPaste as PasteIcon,
  ContentCut as CutIcon, // ✅ CAMBIO: Usamos ícono de Tijera (ContentCut)
  Delete as DeleteIcon, Input as InsertIcon, Warning as WarningIcon,
  ContentPasteGo as CaptureIcon, 
  Collections as BatchIcon 
} from '@mui/icons-material';
import { Editor } from '@tinymce/tinymce-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FixedPage from '../components/FixedPage'; 
import DictationButton from '../components/DictationButton'; 
import { useAuth } from '../context/AuthContext';
import { ClipboardContext } from '../context/ClipboardContext'; 
import { saveReport, getPatientById } from '../services/reportService'; 

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

// --- UTILIDADES ---
const processImageUrl = (url) => { if(!url) return ''; return url.replace('http://', 'https://'); };
const imageToBase64 = async (url) => {
    if (!url || url.startsWith('data:')) return url;
    try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/proxy-image?url=${encodeURIComponent(url)}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) return (await res.json()).base64;
        return url;
    } catch (e) { return url; }
};

// COMPRESOR DE IMÁGENES (EVITA EL COLAPSO DE MEMORIA)
const compressImage = (file) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                // Redimensionar para optimizar memoria
                const MAX_WIDTH = 1200;
                const scaleSize = MAX_WIDTH / img.width;
                const newWidth = (img.width > MAX_WIDTH) ? MAX_WIDTH : img.width;
                const newHeight = (img.width > MAX_WIDTH) ? (img.height * scaleSize) : img.height;

                canvas.width = newWidth;
                canvas.height = newHeight;
                
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, newWidth, newHeight);
                
                // Calidad 0.7 es perfecta para documentos médicos (buena resolución, bajo peso)
                const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
                resolve(compressedDataUrl);
            };
        };
    });
};

// --- CONFIGURACIÓN ESTÁTICA DEL EDITOR ---
const EDITOR_CONFIG = {
    height: '100%',
    menubar: false,
    inline: true, 
    fixed_toolbar_container: '#toolbar-container',
    placeholder: 'Escriba aquí...',
    plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'table', 'help', 'wordcount', 
        'autoresize'
    ],
    toolbar: 'undo redo | lineheight | fontfamily fontsize | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | table image | removeformat',
    fontsize_formats: '6pt 7pt 8pt 9pt 10pt 11pt 12pt 14pt 16pt 18pt 20pt 24pt 36pt',
    
    content_style: `
        body { 
            font-family: Arial, sans-serif; 
            font-size: 11pt; 
            line-height: 1.1; 
            letter-spacing: 0px; 
            color: black; 
            margin: 0; 
            padding: 10px; 
            -webkit-font-smoothing: antialiased; 
            text-rendering: geometricPrecision; 
        }        
        p { 
            margin: 0 0 2px 0; 
            padding: 0; 
            line-height: 1.1;
            text-indent: 0px !important; 
            margin-left: 0px !important; 
        }
        .mce-content-body img { 
            max-width: 48% !important; 
            height: auto; 
            display: inline-block !important; 
            vertical-align: middle; 
            margin: 3px; 
        }
    `,
    forced_root_block: 'p',
    browser_spellcheck: true, 
    contextmenu: false,
    paste_data_images: true,
};

// Componente Editor Aislado
const TinyEditor = React.memo(({ value, onChange, isOverflowing }) => {
    return (
        <Editor
            apiKey="fjnrhmkoepnr0f2fhfyok2y8c6grqx5bmj64mga6erbzi93q"
            value={value}
            onEditorChange={onChange}
            init={{
                ...EDITOR_CONFIG,
                content_style: EDITOR_CONFIG.content_style + ` body { overflow-y: ${isOverflowing ? 'visible' : 'hidden'}; }`
            }} 
        />
    );
});

const EditorInforme = () => {
  const { user } = useAuth();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const clipboardCtx = useContext(ClipboardContext);
  const clipboardItems = clipboardCtx?.clipboardItems || []; 
  const addToClipboard = clipboardCtx?.addToClipboard || (() => {});
  const clearClipboard = clipboardCtx?.clearClipboard || (() => {});
  const removeFromClipboard = clipboardCtx?.removeFromClipboard || (() => {});
  
  // ESTADOS
  const [isInitializing, setIsInitializing] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processStatus, setProcessStatus] = useState(''); 
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [overflowWarning, setOverflowWarning] = useState(null);
  
  // DATOS
  const [pages, setPages] = useState([{ 
      id: 1, 
      content: location.state?.initialContent || '' 
  }]);

  const [headerData, setHeaderData] = useState({ logoUrl: null, patient: null, footerText: '' });
  const [reportMeta, setReportMeta] = useState({ title: 'Informe Médico', reason: '' });
  const [templates, setTemplates] = useState([]);
  
  // DIÁLOGOS
  const [openSaveDialog, setOpenSaveDialog] = useState(false);
  const [openPatientDialog, setOpenPatientDialog] = useState(false);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [openClipboardDialog, setOpenClipboardDialog] = useState(false);
  
  const [luiatroDialog, setLuiatroDialog] = useState({ 
      open: false, 
      question: '', 
      onConfirm: null 
  });

  const [patientsList, setPatientsList] = useState([]);
  
  const fileInputRef = useRef(null);
  const contentImageInputRef = useRef(null); 
  const batchImageInputRef = useRef(null);
  const printContainerRef = useRef(null);

  // --- HELPER PARA ABRIR DIÁLOGO ---
  const askLuiatro = (question, onConfirmAction) => {
      setLuiatroDialog({
          open: true,
          question,
          onConfirm: () => {
              setLuiatroDialog(prev => ({ ...prev, open: false })); 
              onConfirmAction();
          }
      });
  };

  // --- CARGA INICIAL ---
  useEffect(() => {
    const initData = async () => {
      setIsInitializing(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      try {
        const [resConfig, resPatientData, resPatientsList, resTemplates] = await Promise.all([
            fetch(`${API_URL}/api/config`, { headers }),
            patientId ? getPatientById(patientId) : Promise.resolve(null),
            !patientId ? fetch(`${API_URL}/api/patients`, { headers }) : Promise.resolve(null),
            fetch(`${API_URL}/api/medical-templates`, { headers })
        ]);

        if (resConfig.ok) {
          const data = await resConfig.json();
          const conf = Array.isArray(data) ? data[0] : data;
          const logoRaw = conf.logoCartaUrl || conf.logoUrl || conf.headerImageUrl;
          let logoB64 = null;
          if (logoRaw) {
             const processedUrl = processImageUrl(logoRaw);
             logoB64 = await imageToBase64(processedUrl);
          }
          setHeaderData(prev => ({ ...prev, logoUrl: logoB64 || prev.logoUrl, footerText: conf.footerAddress || prev.footerText }));
        }

        if (resPatientData) {
            setHeaderData(prev => ({ ...prev, patient: resPatientData }));
        } else if (resPatientsList && resPatientsList.ok) {
            setPatientsList(await resPatientsList.json());
            setOpenPatientDialog(true);
        }

        if (resTemplates.ok) setTemplates(await resTemplates.json());

      } catch (e) { console.error(e); }
      finally { setIsInitializing(false); }
    };
    initData();
  }, [patientId]);

  // --- FUNCIONES ---
  const handleExit = () => {
      if (location.state?.fromReports) navigate(`/informes/${patientId}`);
      else if (patientId) navigate(`/informes/${patientId}`);
      else navigate('/dashboard');
  };

  const handleAddPage = () => {
      const newPages = [...pages, { id: pages.length + 1, content: '' }];
      setPages(newPages);
      
      if (clipboardItems.length > 0) {
          askLuiatro(
              `Tiene ${clipboardItems.length} elementos en el portapapeles. ¿Desea pegarlos automáticamente en esta nueva página?`,
              () => {
                  setTimeout(() => {
                      const lastIndex = newPages.length - 1;
                      const contentToPaste = clipboardItems.map(item => item.html || item.content || item.value).join('&nbsp;');
                      handlePageChange(lastIndex, contentToPaste);
                      clearClipboard(); 
                      setSuccessMsg("Contenido pegado del portapapeles.");
                  }, 200);
              }
          );
      }
  };

  const handleDeletePage = (indexToDelete) => {
      if (indexToDelete === 0) return; 
      askLuiatro("¿Está seguro de que desea eliminar esta página?", () => {
          setPages(prev => prev.filter((_, idx) => idx !== indexToDelete));
      });
  };
  
  const handlePageChange = useCallback((index, newContent) => {
    setPages(prev => {
        const newPages = [...prev];
        if (newPages[index].content !== newContent) {
            newPages[index].content = newContent;
            return newPages;
        }
        return prev;
    });
    
    if (newContent.length > 3100) { 
        setOverflowWarning({ pageIndex: index });
    } else if (newContent.length < 2900 && overflowWarning?.pageIndex === index) {
        setOverflowWarning(null);
    }
  }, [overflowWarning]);

  const handleDictationResult = (text) => {
      const activeEd = window.tinymce.activeEditor;
      if (activeEd) {
          activeEd.insertContent(text + ' ');
      }
  };

  const handleManualClip = () => {
      const activeEd = window.tinymce.activeEditor;
      if (activeEd) {
          const selection = activeEd.selection.getContent();
          if (selection) {
              addToClipboard({ 
                  id: Date.now(), 
                  commercialName: "Texto Cortado (Manual)", 
                  html: selection, 
                  type: 'cut_content'
              });
              activeEd.selection.setContent(''); 
              setSuccessMsg("✂️ Cortado al portapapeles");
              if (activeEd.getContent().length < 3100) setOverflowWarning(null);
          } else {
              setErrorMsg("Seleccione texto para cortar");
          }
      }
  };

  const handleCaptureImageFromClipboard = async () => {
    try {
        const items = await navigator.clipboard.read();
        let foundImage = false;
        for (const item of items) {
            const types = item.types.filter(t => t.startsWith('image/'));
            if (types.length > 0) {
                const blob = await item.getType(types[0]);
                
                // COMPRESIÓN
                const compressedBase64 = await compressImage(blob);
                
                addToClipboard({
                    id: Date.now(),
                    commercialName: `Img Capturada (${new Date().toLocaleTimeString()})`,
                    html: `<img src="${compressedBase64}" />`,
                    type: 'image'
                });
                setSuccessMsg("📸 Capturada al portapapeles (1)");
                setOpenClipboardDialog(true);
                foundImage = true;
                break;
            }
        }
        if (!foundImage) setErrorMsg("No hay imagen en el portapapeles del sistema.");
    } catch (err) { console.error(err); setErrorMsg("Permita el acceso al portapapeles en el navegador."); }
  };

  const handleBatchImageUpload = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      setIsProcessing(true); 
      setProcessStatus(`Optimizando ${files.length} imágenes...`);

      try {
          let processedCount = 0;
          for (let i = 0; i < files.length; i++) {
              const file = files[i];
              // 1. Comprimir
              const compressedBase64 = await compressImage(file);
              
              // 2. Agregar al portapapeles
              addToClipboard({
                  id: Date.now() + i, 
                  commercialName: file.name,
                  html: `<img src="${compressedBase64}" />`,
                  type: 'image'
              });
              processedCount++;
          }
          
          setSuccessMsg(`✅ ${processedCount} imágenes listas.`);
          setOpenClipboardDialog(true); 
      } catch (err) {
          console.error(err);
          setErrorMsg("Error procesando algunas imágenes.");
      } finally {
          setIsProcessing(false);
          e.target.value = ''; 
      }
  };

  const handlePasteFromClipboard = (htmlContent) => {
      const editor = window.tinymce.activeEditor;
      if (editor) {
          editor.insertContent(htmlContent + '&nbsp;');
          setOpenClipboardDialog(false);
          setSuccessMsg("Imagen insertada.");
      } else {
          setErrorMsg("Haga click en la hoja donde quiere pegar");
      }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) { const r = new FileReader(); r.onload = (ev) => setHeaderData(p => ({ ...p, logoUrl: ev.target.result })); r.readAsDataURL(file); }
  };

  const handleContentImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && window.tinymce?.activeEditor) {
      const r = new FileReader();
      r.onload = (ev) => window.tinymce.activeEditor.insertContent(`<img src="${ev.target.result}" />&nbsp;`);
      r.readAsDataURL(file);
    }
    e.target.value = ''; 
  };

  const handleInsertSignature = async () => {
    setProcessStatus("Firmando..."); setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const resUser = await fetch(`${API_URL}/api/users/${user.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      let freshUser = user;
      if (resUser.ok) freshUser = await resUser.json();
      
      const rawSig = freshUser.signature_url || freshUser.signatureUrl || user?.signature_url;
      let finalSig = null;
      if (rawSig) finalSig = await imageToBase64(processImageUrl(rawSig));

      const name = `${freshUser.prefix || 'Dr.'} ${freshUser.name} ${freshUser.lastname}`;
      const imgHtml = finalSig ? `<img src="${finalSig}" crossOrigin="anonymous" width="120" style="display:block;margin:0 auto;max-width:120px;border:none !important;"/>` : `<div style="text-align:center;font-size:9px;color:#999;">(Sin firma)</div>`;
      
      const sigHTML = `
        <div style="text-align: center; font-family: Arial, sans-serif; margin-top: 25px; page-break-inside: avoid;">
          <div style="margin-bottom: 2px;">${imgHtml}</div>
          <div style="border-top: 1px solid #333; width: 160px; margin: 0 auto 4px auto;"></div>
          <div style="line-height: 1.1;">
            <strong style="font-size: 9pt; text-transform: uppercase;">${name}</strong><br>
            <span style="font-size: 7pt; color: #333;">${freshUser.specialty || 'Médico Cirujano'}</span><br>
            <span style="font-size: 7pt; color: #555;">C.I. ${freshUser.cedula || ''} | MPPS: ${freshUser.mpps_number || ''} | CMA: ${freshUser.college_number || ''}</span>
          </div>
        </div>`;
      
      const activeEd = window.tinymce.activeEditor;
      if (activeEd) activeEd.insertContent(sigHTML);
    } catch(e) { console.error(e); } finally { setIsProcessing(false); }
  };

  const handleInsertTemplate = (tplContent) => {
      const activeEd = window.tinymce.activeEditor;
      if (activeEd) {
          activeEd.insertContent(tplContent);
          setOpenTemplateDialog(false);
      }
  };

  const handleSaveToHistory = async () => {
      if (!headerData.patient) return setErrorMsg("Seleccione un paciente");
      setOpenSaveDialog(false); setIsProcessing(true); setProcessStatus("Guardando...");
      try {
          await saveReport({
              title: reportMeta.title, reason: reportMeta.reason, patientId: headerData.patient.id, createdBy: user.id,
              content: pages.map(p => p.content).join('<div style="page-break-after: always;"></div>')
          });
          setSuccessMsg("✅ Guardado en historial.");
      } catch (e) { setErrorMsg("Error al guardar"); } finally { setTimeout(()=>setIsProcessing(false), 500); }
  };

  const handleSaveToGestion = (isPrintOnly = false) => {
      if (!headerData.patient) return setErrorMsg("Seleccione un paciente primero.");

      if (isPrintOnly) {
          executeProcess(true);
      } else {
          askLuiatro("¿Generar PDF y subir a Gestión Documental permanente?", () => {
              executeProcess(false);
          });
      }
  };

  const executeProcess = async (isPrintOnly) => {
      setIsProcessing(true);
      setProcessStatus("Procesando documento...");

      try {
        const doc = new jsPDF('p', 'mm', 'letter');
        const elements = document.querySelectorAll('.print-page-wrapper'); 
        
        for (let i = 0; i < elements.length; i++) {
            if (i > 0) doc.addPage();
            const element = elements[i];
            
            const canvas = await html2canvas(element, { 
                scale: 2, 
                useCORS: true, 
                backgroundColor: '#ffffff',
                logging: false,
                onclone: (clonedDoc) => {
                    const alerts = clonedDoc.querySelectorAll('.no-print');
                    alerts.forEach(a => a.style.display = 'none');
                    
                    clonedDoc.querySelectorAll('.mce-content-body').forEach(el => {
                        el.style.border = 'none';
                        el.style.boxShadow = 'none';
                        el.style.backgroundColor = 'transparent'; 
                        el.style.color = 'black';
                    });

                    const clonedElement = clonedDoc.querySelector('.print-page-wrapper');
                    if(clonedElement) {
                         clonedElement.style.height = '279.4mm'; 
                    }
                }
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            doc.addImage(imgData, 'JPEG', 0, 0, 215.9, 279.4);
        }

        if (isPrintOnly) {
            const blob = doc.output('blob');
            const url = URL.createObjectURL(blob);
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);
            iframe.onload = () => {
                setTimeout(() => {
                    iframe.contentWindow.print();
                    setIsProcessing(false);
                }, 500);
            };
        } else {
            const pdfBlob = doc.output('blob');
            const formData = new FormData();
            formData.append('file', pdfBlob, `Informe_${headerData.patient.lastName}_${Date.now()}.pdf`);
            formData.append('patientId', headerData.patient.id);
            formData.append('title', reportMeta.title || 'Informe Médico');
            formData.append('category', 'INFORME');

            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/documents`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (res.ok) {
                setSuccessMsg("✅ PDF guardado.");
                setIsProcessing(false);
                setTimeout(() => {
                    askLuiatro("Documento guardado exitosamente. ¿Desea ir a la carpeta de Documentos del paciente?", () => {
                        navigate('/documents');
                    });
                }, 500);
            }
        }
    } catch (e) { 
        console.error(e);
        setErrorMsg("Error al procesar."); 
        setIsProcessing(false);
    }
  };

  return (
    <>
      <style>{`
  @media print {
    @page { size: letter; margin: 0; }
    body { margin: 0; padding: 0; background: white; -webkit-print-color-adjust: exact; }
    .no-print, .tox-toolbar, .tox-editor-header, .MuiDialog-root, .MuiBackdrop-root { display: none !important; }
    
    .print-page-wrapper {
      display: block !important; visibility: visible !important; width: 215.9mm; height: 279.4mm;
      page-break-after: always; position: relative; margin: 0; padding: 0;
    }
    
    .mce-content-body { 
      border: none !important; 
      color: black !important; 
      padding: 10 !important; 
      letter-spacing: 0px !important; 
      line-height: 1.1 !important; 
      font-size: 11pt !important;
      text-rendering: geometricPrecision !important;
      -webkit-font-smoothing: antialiased !important;
    }
  }
        .mce-content-body { border: 1px dashed #ccc; min-height: 100%; padding: 10px; }
        .mce-content-body:focus { border: 1px solid #1976d2; background-color: #fff; outline: none; }
        .page-overflow-container {
            height: auto !important; min-height: 100% !important; border-bottom: 3px solid red;
            background-color: #fff0f0; padding-bottom: 50px;
        }
      `}</style>

      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={isInitializing || isProcessing}>
          <CircularProgress color="inherit" />
          <Typography sx={{ml:2}}>{isInitializing ? 'Cargando...' : processStatus}</Typography>
      </Backdrop>

      <Box className="no-print" sx={{ position: 'sticky', top: 0, zIndex: 1100, bgcolor: 'white', borderBottom: '1px solid #ccc', boxShadow: 1 }}>
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" color="error" startIcon={<ExitIcon />} onClick={handleExit} sx={{ mr: 2 }}>SALIR</Button>
            <Button variant="outlined" size="small" onClick={() => fileInputRef.current.click()}>Logo</Button>
            <Button variant="outlined" color="secondary" size="small" startIcon={<TemplateIcon />} onClick={() => setOpenTemplateDialog(true)}>Plantillas</Button>
            <Button variant="outlined" size="small" startIcon={<SearchIcon />} onClick={() => setOpenPatientDialog(true)}>
                {headerData.patient ? `${headerData.patient.name} ${headerData.patient.lastName}` : 'Paciente'}
            </Button>
            
            <DictationButton onTranscription={handleDictationResult} /> 
            
            <Tooltip title="Portapapeles Médico">
                <IconButton color={clipboardItems.length > 0 ? "primary" : "default"} onClick={() => setOpenClipboardDialog(true)}>
                    <Badge badgeContent={clipboardItems.length} color="error"><PasteIcon /></Badge>
                </IconButton>
            </Tooltip>
            
            <Tooltip title="Cortar Selección a Portapapeles">
                <IconButton onClick={handleManualClip} color="secondary"><CutIcon /></IconButton> {/* ✅ ÍCONO TIJERA AQUÍ */}
            </Tooltip>

            {/* BOTÓN CAPTURA INDIVIDUAL */}
            <Tooltip title="Capturar 1 imagen del Portapapeles">
                <IconButton color="warning" onClick={handleCaptureImageFromClipboard}>
                    <CaptureIcon />
                </IconButton>
            </Tooltip>

            {/* BOTÓN CARGA MASIVA */}
            <Button 
                variant="outlined" 
                color="secondary" 
                size="small" 
                startIcon={<BatchIcon />} 
                onClick={() => batchImageInputRef.current.click()}
            >
                Importar Lote
            </Button>

            <Button variant="outlined" size="small" startIcon={<PhotoIcon />} onClick={() => contentImageInputRef.current.click()}>Imagen</Button>
            <Button variant="outlined" size="small" startIcon={<DrawIcon />} onClick={handleInsertSignature}>Firma</Button>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddPage}>Pag +</Button>
            
            <div style={{ flex: 1 }}></div>
            
            <Button variant="contained" color="success" startIcon={<SaveIcon />} onClick={() => setOpenSaveDialog(true)}>Guardar</Button>
            <Button variant="contained" color="info" startIcon={<CloudUploadIcon />} onClick={() => handleSaveToGestion(false)}>NUBE PDF</Button>
            <Button variant="contained" color="primary" startIcon={<PrintIcon />} onClick={() => handleSaveToGestion(true)}>IMPRIMIR</Button>
         </Box>
         
         <Box id="toolbar-container" sx={{ minHeight: '40px', bgcolor: '#f0f2f5', borderTop: '1px solid #ddd', pl: 1 }}></Box>
         <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleLogoUpload} />
         <input type="file" ref={contentImageInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleContentImageUpload} />
         <input type="file" ref={batchImageInputRef} style={{ display: 'none' }} accept="image/*" multiple onChange={handleBatchImageUpload} />
      </Box>

      <Box sx={{ bgcolor: '#525659', minHeight: 'calc(100vh - 110px)', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', overflowY: 'auto' }}>
        {pages.map((page, index) => (
          <div key={index} className="print-page-wrapper" ref={index === 0 ? printContainerRef : null} style={{position:'relative'}}>
            {overflowWarning?.pageIndex === index && (
                <Alert 
                    severity="error" 
                    className="no-print"
                    action={<Button color="inherit" size="small" onClick={() => setOverflowWarning(null)}>OCULTAR</Button>}
                    sx={{ mb: 1, border: '2px solid red' }}
                >
                    ⚠️ <strong>DESBORDAMIENTO:</strong> Seleccione el texto sobrante al final (área roja) y use el botón de cortar (tijera).
                </Alert>
            )}

            <FixedPage pageNumber={index + 1} totalPages={pages.length} headerData={headerData}>
              {index > 0 && (
                  <IconButton 
                      className="no-print"
                      onClick={() => handleDeletePage(index)}
                      sx={{ position: 'absolute', top: 5, right: 5, color: '#d32f2f', zIndex: 100 }}
                      size="small"
                  >
                      <DeleteIcon fontSize="small" />
                  </IconButton>
              )}
            
              <Box sx={{ height: '100%', position: 'relative' }} className={overflowWarning?.pageIndex === index ? 'page-overflow-container' : ''}>
                  <TinyEditor 
                    value={page.content} 
                    onChange={(val) => handlePageChange(index, val)} 
                    isOverflowing={overflowWarning?.pageIndex === index}
                  />
              </Box>
            </FixedPage>
          </div>
        ))}
      </Box>

      <Dialog open={openPatientDialog} onClose={() => setOpenPatientDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Seleccionar Paciente</DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            options={patientsList}
            getOptionLabel={(option) => `${option.name} ${option.lastName} (${option.cedula || 'S/C'})`}
            onChange={(e, v) => { if(v) { setHeaderData(prev => ({...prev, patient: v})); setOpenPatientDialog(false); }}}
            renderInput={(params) => <TextField {...params} label="Buscar paciente" autoFocus />}
          />
        </DialogContent>
        <DialogActions><Button onClick={() => setOpenPatientDialog(false)}>Cancelar</Button></DialogActions>
      </Dialog>

      <Dialog open={openSaveDialog} onClose={() => setOpenSaveDialog(false)}>
        <DialogTitle>Guardar Informe</DialogTitle>
        <DialogContent>
            <TextField autoFocus margin="dense" label="Título" fullWidth value={reportMeta.title} onChange={(e) => setReportMeta({...reportMeta, title: e.target.value})} />
            <TextField margin="dense" label="Observación" fullWidth multiline rows={2} value={reportMeta.reason} onChange={(e) => setReportMeta({...reportMeta, reason: e.target.value})} />
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpenSaveDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveToHistory} variant="contained" color="success">Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openTemplateDialog} onClose={() => setOpenTemplateDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Insertar Plantilla</DialogTitle>
        <DialogContent dividers>
            <List>
                {templates.map((tpl) => (
                    <ListItem button key={tpl.id} onClick={() => handleInsertTemplate(tpl.content)}>
                        <ListItemIcon><TemplateIcon color="primary"/></ListItemIcon>
                        <ListItemText primary={tpl.name} secondary={tpl.category} />
                    </ListItem>
                ))}
            </List>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpenTemplateDialog(false)}>Cerrar</Button></DialogActions>
      </Dialog>

      <Dialog open={openClipboardDialog} onClose={() => setOpenClipboardDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>Portapapeles Médico (Total: {clipboardItems.length})</DialogTitle>
        <DialogContent dividers>
            <List>
                {clipboardItems.map((item, idx) => {
                    // ✅ LÓGICA DE MINIATURA:
                    // Si es imagen, parseamos el HTML para extraer el src y mostrar miniatura
                    let preview = null;
                    if (item.type === 'image' && item.html) {
                        // Extraemos la URL de la imagen del string HTML
                        const match = item.html.match(/src="([^"]*)"/);
                        if (match && match[1]) {
                            preview = (
                                <img 
                                    src={match[1]} 
                                    alt="Miniatura" 
                                    style={{ 
                                        height: '60px', 
                                        width: 'auto', 
                                        borderRadius: '4px',
                                        border: '1px solid #ddd',
                                        objectFit: 'contain'
                                    }} 
                                />
                            );
                        }
                    } else {
                        // Si es texto, mostramos snippet
                        preview = (
                            <div 
                                style={{ maxHeight: '60px', overflow: 'hidden', fontSize: '0.8rem', color: '#666' }} 
                                dangerouslySetInnerHTML={{__html: item.html || item.content}} 
                            />
                        );
                    }

                    return (
                        <ListItem key={item.cartId || idx} divider>
                            <ListItemText 
                                primary={item.commercialName || "Elemento"} 
                                // Renderizamos la miniatura o el texto según corresponda
                                secondary={preview} 
                            />
                            <ListItemSecondaryAction>
                                <Button variant="contained" size="small" startIcon={<InsertIcon />} onClick={() => handlePasteFromClipboard(item.html || item.content)} sx={{ mr: 1 }}>Insertar</Button>
                                <IconButton edge="end" onClick={() => removeFromClipboard(item.cartId)}><DeleteIcon /></IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    );
                })}
                {clipboardItems.length === 0 && <Typography sx={{p:2, color: '#777'}}>El portapapeles está vacío.</Typography>}
            </List>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => clearClipboard()} color="error">Limpiar Todo</Button>
            <Button onClick={() => setOpenClipboardDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* ✅ DIÁLOGO DE CONFIRMACIÓN PROFESIONAL "Luiatro Health" */}
      <Dialog 
        open={luiatroDialog.open} 
        onClose={() => setLuiatroDialog(prev => ({ ...prev, open: false }))}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: '#1976d2' }}>
            <WarningIcon color="primary" /> Luiatro Health dice:
        </DialogTitle>
        <DialogContent dividers>
            <Typography variant="body1">{luiatroDialog.question}</Typography>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setLuiatroDialog(prev => ({ ...prev, open: false }))} color="inherit">
                Cancelar
            </Button>
            <Button onClick={luiatroDialog.onConfirm} variant="contained" color="primary">
                Aceptar
            </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!successMsg} autoHideDuration={4000} onClose={() => setSuccessMsg('')}><Alert severity="success">{successMsg}</Alert></Snackbar>
      <Snackbar open={!!errorMsg} autoHideDuration={4000} onClose={() => setErrorMsg('')}><Alert severity="error">{errorMsg}</Alert></Snackbar>
    </>
  );
};

export default EditorInforme;